import { z } from "zod";
import { PaginationRequestSchema, PaginationResponseSchema } from "./pagination";

// Season Schema
export const SeasonSchema = z.object({
  season: z.string().min(1, "La temporada es requerida"),
  start: z.coerce.date().optional(),
  end: z.coerce.date().optional(),
  statusId: z.string().optional(),
  formatId: z
    .string()
    .min(1, "La referencia de formato es requerida")
    .optional(),
  isCurrent: z.boolean().optional(),
  coverageConfig: z.any().optional(),
  currentStageOrder: z.number().optional(),
});

export const QualificationSlotSchema = z
  .object({
    slot: z.string().min(1, "El slot es requerido"),
    source: z.enum(["GROUP_RANK", "BEST_THIRD"]),
    group: z.string().min(1, "El grupo es requerido").optional(),
    pool: z.array(z.string()).optional(),
    rank: z.number().min(1, "El rango es requerido").optional(),
  })
  .superRefine((data, ctx) => {
    const hasGroup = Boolean(data.group);
    const hasPool = Boolean(data.pool && data.pool.length);

    if (!hasGroup && !hasPool) {
      ctx.addIssue({
        code: "custom",
        message: "Debe especificar un grupo o un pool",
      });
    }

    if (hasGroup && hasPool) {
      ctx.addIssue({
        code: "custom",
        message: "No puede especificar grupo y pool al mismo tiempo",
      });
    }

    if (hasGroup && data.source !== "GROUP_RANK") {
      ctx.addIssue({
        code: "custom",
        message: "Source debe ser GROUP_RANK cuando se especifica un grupo",
      });
    }

    if (hasPool && data.source !== "BEST_THIRD") {
      ctx.addIssue({
        code: "custom",
        message: "Source debe ser BEST_THIRD cuando se especifica un pool",
      });
    }

    if (hasGroup && data.rank == null) {
      ctx.addIssue({
        code: "custom",
        message: "El rango es requerido cuando se define un grupo",
      });
    }
  });

export type QualificationSlotDTO = z.infer<typeof QualificationSlotSchema>;

// Detailed status object used in paginated responses
export const StatusDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  color: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number(),
});

export const SeasonWithStatusSchema = z.object({
  id: z.string(),
  season: z.string(),
  start: z.coerce.date().optional(),
  end: z.coerce.date().optional(),
  status: StatusDetailSchema.nullable().optional(),
  formatId: z.string(),
  format: z
    .object({
      id: z.string(),
      name: z.string(),
      sport: z.string(),
      config: z.any(),
      createdAt: z.coerce.date(),
    })
    .nullable()
    .optional(),
  isCurrent: z.boolean().optional(),
  coverageConfig: z.any().optional(),
  currentStageOrder: z.number().optional(),
});

export const CreateSeasonRequestSchema = z.object({
  tournamentId: z.string().min(1, "El torneo es requerido"),
  season: z.string().min(1, "La temporada es requerida"),
  start: z.coerce.date().optional(),
  end: z.coerce.date().optional(),
  statusId: z.string().optional(),
  formatId: z
    .string()
    .min(1, "La referencia de formato es requerida")
    .optional(),
  isCurrent: z.boolean().optional(),
});

export const UpdateSeasonRequestSchema = CreateSeasonRequestSchema.partial();

export type CreateSeasonRequestDTO = z.infer<typeof CreateSeasonRequestSchema>;
export type UpdateSeasonRequestDTO = z.infer<typeof UpdateSeasonRequestSchema>;

export const SeasonQuerySchema = PaginationRequestSchema.extend({
  season: z.string().optional(),
  tournamentId: z.string().optional(),
});
export type SeasonQueryDTO = z.infer<typeof SeasonQuerySchema>;

export const SeasonResponseSchema = z.object({
  id: z.string(),
  season: z.string(),
  start: z.coerce.date().nullable(),
  end: z.coerce.date().nullable(),
  statusId: z.string().optional(),
  formatId: z.string(),
  isCurrent: z.boolean(),
  currentStageOrder: z.number().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type SeasonResponseDTO = z.infer<typeof SeasonResponseSchema>;

export const ChangeSeasonStatusRequestSchema = z.object({
  statusId: z.string().nullable(),
});

export const SeasonsPaginatedResponseSchema =
  PaginationResponseSchema(SeasonResponseSchema);
export type SeasonsPaginatedResponseDTO = z.infer<
  typeof SeasonsPaginatedResponseSchema
>;
